"use client";

import Link from "next/link";
import { useId } from "react";
import { DatenZeile, KartenListe, TABELLEN_RAHMEN, aktionenKopfKlasse, kopfKlasse, namensKopfKlasse } from "./tabellen-teile";
import type { TabellenDichte, TabellenSpalte, TabellenZeile } from "./tabellen-typen";

/** Kompakte Tabellen haben immer die Dichte „kompakt“ (ein Zeilenbild, keine Zweitzeile). */
const DICHTE: TabellenDichte = "kompakt";

/**
 * Kompakte Tabelle H (M-003, `tabellen.md` Archetyp H, T11/T13): 3–7 Zeilen in einer Karte (Startseite, Detail,
 * Projekt-Startseite „Teilprojekte“), ohne Filter, Sortierung und Blättern. Kopf: Titel + Anzahl-Pille; Spaltenkopf
 * sichtbar erst ab drei Spalten (sonst nur `<caption class="sr-only">`). Fuß: Link „Alle <n> anzeigen“ zur gefilterten
 * T2-Liste oder Knopf „Mehr laden“. Leer: ein Satz ohne Aktion. Unter 768 px Karten-Zeilen (T12).
 */
export function KompaktTabelle({
  id,
  titel,
  titelLeise,
  beschriftung,
  anzahl,
  namensSpalte,
  spalten,
  zeilen,
  fuss,
  leerText,
  kopfAktionen,
}: {
  id?: string;
  titel: string;
  /** Kartentitel im leisen Stil (`font-medium`) wie `SegmentStatusKarte`/`BalkenKarte`, für Seiten mit gemischten Karten (CMDB-Übersicht). */
  titelLeise?: boolean;
  beschriftung?: string;
  anzahl?: number;
  namensSpalte: string;
  /** Weitere Spalten (ohne Sortierknopf, `sortierbar` wird ignoriert). */
  spalten: TabellenSpalte[];
  zeilen: TabellenZeile[];
  /** Fußlink („Alle 23 anzeigen“, `href`) oder „Mehr laden“ (`onKlick`). */
  fuss?: { text: string; href?: string; onKlick?: () => void };
  leerText: string;
  /**
   * Aktionen im Kartenkopf rechts (z. B. „Teilprojekt anlegen/zuordnen“, M-004 TP7), vom Aufrufer nur mit Recht gesetzt
   * (r110). Die Aktion erscheint auch bei leerer Tabelle; Wortlaut und Rechtefrage liegen beim Aufrufer.
   */
  kopfAktionen?: React.ReactNode;
}) {
  const auto = useId();
  const basis = id ?? auto;
  const titelId = `${basis}-titel`;
  const mitAktionen = zeilen.some((z) => z.aktionen);
  const mitKopf = spalten.length + 1 >= 3;

  return (
    <>
      {/* Preline: component/tables/tables-rounded-tables (kompakt, in Karte nach dashboards/admin-dashboard/index.html#browsers-card) */}
      <section id={id} aria-labelledby={titelId} className="flex flex-col bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden">
        <div className="p-4 pb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 id={titelId} className={titelLeise ? "font-medium text-foreground" : "text-lg font-semibold text-foreground"}>
            {titel}
          </h2>
          {typeof anzahl === "number" && anzahl > 0 && (
            <span className="py-1 ps-1.5 pe-2 inline-flex items-center gap-x-1 text-xs font-medium rounded-full bg-layer border border-layer-line text-layer-foreground">
              {anzahl}
            </span>
          )}
          {kopfAktionen && <div className="w-full sm:w-auto sm:ms-auto">{kopfAktionen}</div>}
        </div>

        {zeilen.length === 0 ? (
          <div className="p-5 flex flex-col justify-center items-center text-center" role="status">
            <p className="text-sm text-muted-foreground-1">{leerText}</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <div className={TABELLEN_RAHMEN}>
                <table className="min-w-full">
                  <caption className="sr-only">{beschriftung ?? titel}</caption>
                  {mitKopf && (
                    <thead>
                      <tr>
                        <th scope="col" className={namensKopfKlasse(DICHTE)}>
                          {namensSpalte}
                        </th>
                        {spalten.map((s) => (
                          <th key={s.id} scope="col" className={kopfKlasse(DICHTE, s.ausrichtung)}>
                            {s.kopf}
                          </th>
                        ))}
                        {mitAktionen && (
                          <th scope="col" className={aktionenKopfKlasse(DICHTE)}>
                            <span className="sr-only">Aktionen</span>
                          </th>
                        )}
                      </tr>
                    </thead>
                  )}
                  <tbody className="divide-y divide-table-line">
                    {zeilen.map((z) => (
                      <DatenZeile key={z.id} zeile={z} spalten={spalten} dichte={DICHTE} mitAktionen={mitAktionen} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <KartenListe zeilen={zeilen} spalten={spalten} />
          </>
        )}

        {fuss && (
          <div className="text-center border-t border-card-footer">
            {fuss.href ? (
              <Link
                className="p-3 flex justify-center items-center gap-x-2 text-sm text-primary rounded-b-lg hover:text-primary-hover focus:outline-hidden focus:decoration-2 focus:underline focus:text-primary-focus"
                href={fuss.href}
              >
                {fuss.text}
              </Link>
            ) : (
              <button
                type="button"
                className="w-full p-3 flex justify-center items-center gap-x-2 text-sm text-primary rounded-b-lg hover:text-primary-hover focus:outline-hidden focus:decoration-2 focus:underline focus:text-primary-focus"
                onClick={fuss.onKlick}
              >
                {fuss.text}
              </button>
            )}
          </div>
        )}
      </section>
      {/* End Kompakt */}
    </>
  );
}
